import dom from '../utils/dom';
import { loadModal } from '../AsyncLoader';
import Service from './AddressVerificationService';
import ContentStorage, { ComponentKeys } from '../tnew/content/ContentStorage';

/** @typedef {import("./types").Address} Address */
/** @typedef {import("./types").IAddressOption} IAddressOption */
/** @typedef {import('../tnew/ui/Modal/FormModalHandle').default} FormModalHandle */

const SINGLE_ADDRESS_SUGGESTION_ID = 'tn-address-verification-form-single-suggestion';
const SUGGESTED_ADDRESSES_ID = 'tn-suggested-addresses';

const RadioOption = {
  USE_SELECTED: 'useSelected',
  USE_AS_ENTERED: 'useAsEntered',
  ALLOW_EDIT: 'allowEdit',
};

function getAvsComponentData() {
  const content = (property) => ContentStorage.get(ComponentKeys.ADDRESS_VERIFICATION_SERVICE, property);

  return {
    subhead: content('Subhead'),
    subheadText: content('SubheadText'),
    useSelectedAddressLabel: content('UseSelectedAddressLabel'),
    useSuggestedAddressLabel: content('UseSuggestedAddressLabel'),
    useEnteredAddressLabel: content('UseEnteredAddressLabel'),
    useEditedAddressLabel: content('UseEditedAddressLabel'),
    submitButtonLabel: content('SubmitButtonLabel'),
  };
}

/**
 * @param {Array.<IAddressOption>} options
 */
function buildSuggestionsElement(options) {
  if (options.length === 1) {
    const $div = document.createElement('div');
    $div.setAttribute('class', 'alert alert-info');
    $div.setAttribute('id', SINGLE_ADDRESS_SUGGESTION_ID);
    $div.textContent = options[0].addressString;

    return $div;
  }

  const $select = document.createElement('select');
  $select.classList.add('form-control');
  $select.id = (SUGGESTED_ADDRESSES_ID);

  options.forEach((value) => {
    const $option = document.createElement('option');
    $option.textContent = value.addressString;
    $option.value = value.key;
    $select.appendChild($option);
  });

  return $select;
}

export class SuggestedAddressesSelectionResponse {
  /**
   * @param {Address} confirmedAddress
   * @param {FormModalHandle} modal
   * @param {boolean} wasCancelled
   */
  constructor(confirmedAddress, modal, wasCancelled) {
    this.confirmedAddress = confirmedAddress;
    this.modal = modal;
    this.wasCancelled = wasCancelled;
  }
}

/**
 * @param {Array.<IAddressOption>} suggestedAddresses
 * @param {Address} userEnteredAddress
 * @returns {Promise<SuggestedAddressesSelectionResponse>}
 */
async function getSuggestedAddressesSelection(suggestedAddresses, userEnteredAddress) {
  const Modal = await loadModal();

  const {
    submitButtonLabel,
    subhead,
    subheadText,
    useSelectedAddressLabel,
    useSuggestedAddressLabel,
    useEnteredAddressLabel,
    useEditedAddressLabel,
  } = getAvsComponentData();

  const OPTION_NAME = 'tn-address-verification-option';
  const SUGGESTIONS_CONTAINER_CLASS = 'tn-address-verification-form__suggested-address-container';
  const AVS_LEGEND_ID = 'tn-address-verification-form-legend';

  const $form = dom.parseElement(`
    <form id="tn-address-verification-form">
      <fieldset>
        <legend id="${AVS_LEGEND_ID}" class="tn-address-verification-form__subhead">${subheadText}</legend>
        <section class="tn-address-verification-form__option-list" aria-labelledby="${AVS_LEGEND_ID}">
          <div class="tn-address-verification-form__option-list-item">
            <div class="radio">
              <label><input type="radio" name="${OPTION_NAME}" value="${RadioOption.USE_SELECTED}" checked="checked">${useSelectedAddressLabel}</label>
              <div class="${SUGGESTIONS_CONTAINER_CLASS}"> 
               <label for="${SUGGESTED_ADDRESSES_ID}">${useSuggestedAddressLabel}</label> 
              </div>
            </div>
          </div>
          <div class="tn-address-verification-form__option-list-item">
            <div class="radio">
              <label><input type="radio" name="${OPTION_NAME}" value="${RadioOption.USE_AS_ENTERED}">${useEnteredAddressLabel}</label>
            </div>
          </div>
          <div class="tn-address-verification-form__option-list-item">
            <div class="radio">
              <label><input type="radio" name="${OPTION_NAME}" value="${RadioOption.ALLOW_EDIT}">${useEditedAddressLabel}</label>
            </div>
          </div>
        </section>
      </fieldset>
    </form>
  `);

  const $radios = dom.findAllFrom($form, `input[name="${OPTION_NAME}"]`);
  const $suggestions = buildSuggestionsElement(suggestedAddresses);

  dom.findFrom($form, `.${SUGGESTIONS_CONTAINER_CLASS}`).appendChild($suggestions);

  // if there is only one suggested address, describe the radiobutton for using the suggested address by those address details
  if (suggestedAddresses.length === 1) {
    const $radio = dom.findFrom($form, `input[value="${RadioOption.USE_SELECTED}"]`);
    $radio.setAttribute('aria-describedby', SINGLE_ADDRESS_SUGGESTION_ID);
  }

  return new Promise((resolve) => {
    let modal;

    const onSubmit = async () => {
      /** @type {Address} */
      let confirmedAddress = null;

      const selectedOption = $radios.find(($el) => $el.checked).value;

      if (selectedOption === RadioOption.USE_SELECTED) {
        let addressData;

        if (suggestedAddresses.length === 1) {
          addressData = suggestedAddresses[0];
        } else {
          const selectedAddressKey = $suggestions.value;

          addressData = suggestedAddresses.find((el) => el.key === selectedAddressKey);
        }

        confirmedAddress = await Service.getAddress(addressData.requestedCountryValue, addressData.key);
      } else if (selectedOption === RadioOption.USE_AS_ENTERED) {
        confirmedAddress = userEnteredAddress;
      }

      const wasCancelled = confirmedAddress === null;

      resolve(new SuggestedAddressesSelectionResponse(confirmedAddress, modal, wasCancelled));
    };

    const defaultReturnFocusEl = document.activeElement;
    const getReturnFocusEl = () => {
      const selectedOption = $radios.find(($el) => $el.checked).value;
      if (selectedOption === RadioOption.ALLOW_EDIT) {
        return dom.findFrom(document, '.tn-address-component input:first-of-type');
      }
      return defaultReturnFocusEl;
    };

    modal = Modal.presentForm({
      title: subhead,
      labelBtnSubmit: submitButtonLabel,
      labelBtnCancel: null,
      $form,
      onSubmit,
      closeOnEscape: false,
      closeOnBackdrop: false,
      closeButtonInHeader: false,
      returnFocusTo: getReturnFocusEl,
    });
  });
}

export default getSuggestedAddressesSelection;
